import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db, database } from '../firebase';
import { collection, addDoc, serverTimestamp as firestoreTimestamp, getDocs, query, where, doc, getDoc, updateDoc } from 'firebase/firestore';
import { ref, onValue, get, set, update, runTransaction, serverTimestamp as rtdbTimestamp } from 'firebase/database';
import { NavalConstants } from '../constants/NavalConstants';
import vortexDocsService, { estimateQuestionsForText } from '../services/vortexDocsService';
import pdfToText from 'react-pdftotext';
import mammoth from 'mammoth';

import { useAuth } from '../hooks/useAuth';
import { INSTITUTION_PROMPTS } from '../constants/institutionPrompts';
import { saveAcademyQuiz } from '../services/academyQuizzes';
import { invalidateStudyQuestionsCache } from '../utils/studyDataService';

// Mapeo manual entre índices de RTDB y códigos de Firestore (Portado de Vault.jsx)
const RTDB_TO_FIRESTORE_HIERARCHY = {
  '0': 'MRO', '1': 'CBO', '2': '3RM', '3': '2DM', '4': '1RM', '5': 'TTE.CORB.', '6': 'TTE.FRAG.', '7': 'TTE.NAV.', '8': 'CAP.CORB.'
};

const RTDB_TO_FIRESTORE_CORPS = {
  '0': 'CG', '1': 'IM', '2': 'AN', '3': 'MN'
};

const RTDB_TO_FIRESTORE_SERVICES = {
  '0': 'SAIN', '1': 'SAN', '2': 'ING', '3': 'JUS', '4': 'DOC', '5': 'TS', '6': 'MUS', '7': 'SG'
};

const SPECIALTY_NAME_TO_CODE = {
  'Oficinista': 'OFTA', 'Oficinistas': 'OFTA', 'Infanteria de Marina': 'IM', 'Infantería de Marina': 'INFANTERÍA DE MARINA', 'Lavandería': 'LAVD', 'Cocina': 'COCN',
  'Armamento': 'ARMM', 'Navegación': 'NAVG', 'Condestable': 'COND', 'Mecánico': 'MEC', 'Electricista': 'ELEC',
  'Electrónica': 'ELTC', 'Torpedos': 'TORP', 'Comunicaciones': 'COM', 'Radar': 'RAD', 'Sonar': 'SON',
  'Contramedidas': 'CMED', 'Piloto Aviador Naval': 'PAN', 'Observador Aéreo': 'OAN', 'Técnico Aeronáutico': 'TAN',
  'Fusilero': 'FUS', 'Artillero': 'ART', 'Médico': 'MED', 'Enfermero': 'ENF', 'Odontólogo': 'ODO', 'Farmacéutico': 'FAR',
  'Costurero': 'COSTR', 'Sastre': 'SSTR', 'Chofer': 'CHOFR', 'Lavandero': 'LAVDR', 'Jardinero': 'JRDNR',
  'Servicios Generales de alimentacion': 'SGA', 'impresor': 'IMPR', 'Camarero': 'CMRR', 'Cocinero': 'COCN',
  'Panadero': 'PNDR', 'Peluquero': 'PLQR', 'Armamento Aereo': 'ARMM_AER', 'Mecánico de Aviación': 'MEC_AVI',
  'Electrónico de Aviación': 'ELE_AVI', 'Operaciones Aereas': 'OPE_AER', 'Controlador de Vuelo': 'CTRL_VLO',
  'Contramaestres': 'CONTRAM', 'Condestables': 'CONDEST', 'Maquinas': 'MAQ', 'Trabajos Submarinos': 'TRAB_SUB',
  'Proteccion Portuaria': 'PROT_PORT', 'Informatico': 'INF', 'Radio Operador': 'RAD_OP', 'Mecánico Automotriz': 'MEC_AUTO',
  'Hidrografía': 'HIDRO', 'Informática/Computación': 'INF_COMP', 'carpintero ': 'CRPNT', 'Tornero': 'TRNR',
  'Soldador': 'SLDR', 'Electromecanico': 'E_MEC', 'Fontanero': 'FONT', 'Criminalistica': 'CRIM',
  'Licenciado en Derecho': 'DER', 'Mecánicos/Motoristas': 'MEC_MOT', 'Electricistas': 'ELEC',
  'Frigoríficos': 'FRIG', 'Control de Averías': 'CTRL_AV', 'Electrónicos': 'ELECTRON', 'Médicos Cirujanos': 'MED_CIR',
  'Cirujanos Dentistas': 'CIR_DENT', 'Enfermería': 'ENF', 'Psicología': 'PSIC', 'Nutrición': 'NUTR', 'Sanidad': 'SAN',
  'camillero ': 'CAMILL', 'Servicios Generales de Sanidad': 'SGS', 'Químico': 'QUIM', 'Dentista': 'DENT',
  'Radiologo': 'RADIO', 'Laboratorista': 'LAB', 'Licenciada en Pedagogia': 'PEDAG', 'Licenciada en Educacion Preescolar': 'EDUC_PRE',
  'Licenciada en Educacion Primaria': 'EDUC_PRIM', 'Alimentación': 'ALIM', 'Servicios Generales': 'SG',
  'Servicio de Trabajo Social Naval': 'TSN'
};

const getRTDBPrefix = (inst) => {
  return (!inst || inst === 'SEMAR') ? '' : `content/${inst.toLowerCase()}`;
};

const VortexDocs = () => {
  const navigate = useNavigate();
  const { user, profileData } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [extractedText, setExtractedText] = useState('');
  const [summary, setSummary] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileName, setFileName] = useState('');
  const [availableModels, setAvailableModels] = useState({});
  const [selectedModel, setSelectedModel] = useState('');
  const [currentModelInfo, setCurrentModelInfo] = useState(null);
  const [isLoadingModels, setIsLoadingModels] = useState(true);
  const [modelLoadError, setModelLoadError] = useState('');
  const fileInputRef = useRef(null);

  // New states for configuration and hierarchy
  const [mode, setMode] = useState('general'); // 'general' or 'specialty'
  const [numQuestions, setNumQuestions] = useState(10);
  const [difficulty, setDifficulty] = useState('medium');
  const [areas, setAreas] = useState([]);
  const [temas, setTemas] = useState([]);
  const [appMetadata, setAppMetadata] = useState(null);
  const [specialtyTemas, setSpecialtyTemas] = useState({});

  // Selection states
  const [selectedAreaId, setSelectedAreaId] = useState('');
  const [selectedGeneralTemaId, setSelectedGeneralTemaId] = useState('');
  const [customTemaName, setCustomTemaName] = useState('');
  const [specialtyJerarquia, setSpecialtyJerarquia] = useState('');
  const [specialtyCuerpo, setSpecialtyCuerpo] = useState('');
  const [specialtyEspecialidad, setSpecialtyEspecialidad] = useState('');
  const [specialtyTemaId, setSpecialtyTemaId] = useState('');

  // Metadata options and mappings
  const [hierarchyOptions, setHierarchyOptions] = useState([]);
  const [corpsOptions, setCorpsOptions] = useState([]);
  const [specialtyOptions, setSpecialtyOptions] = useState([]);
  const [hierarchyMap, setHierarchyMap] = useState({ nameToCode: {}, codeToName: {} });
  const [corpsMap, setCorpsMap] = useState({ nameToCode: {}, codeToName: {} });
  const [specialtyMap, setSpecialtyMap] = useState({ nameToCode: {}, codeToName: {} });
  const [specialtiesByCorps, setSpecialtiesByCorps] = useState({});
  const [allSpecialties, setAllSpecialties] = useState([]);

  // Generation and Preview states
  const [generatedQuestions, setGeneratedQuestions] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [generationStep, setGenerationStep] = useState('');
  const [generationProgress, setGenerationProgress] = useState(0);
  const [saveProgress, setSaveProgress] = useState(0);
  const [publishProgress, setPublishProgress] = useState(0);
  const [isVaultPublishing, setIsVaultPublishing] = useState(false);
  const [isGlobalVaultPublishing, setIsGlobalVaultPublishing] = useState(false);
  const [previewAnswers, setPreviewAnswers] = useState({}); // Tracking selected answers in preview

  // Cargar datos al inicializar
  useEffect(() => {
    loadModels();
  }, []);

  // Escuchar metadatos y estructuras según institución
  useEffect(() => {
    if (user) {
      loadHierarchyData();
    }
  }, [user, profileData?.institucion]);

  const createBidirectionalMap = (metadataObject) => {
    const nameToCode = {};
    const codeToName = {};
    Object.entries(metadataObject).forEach(([code, value]) => {
      const name = typeof value === 'object' ? (value.nombre || code) : value;
      const upperCode = code.toUpperCase();
      nameToCode[name] = upperCode;
      nameToCode[name.toUpperCase()] = upperCode;
      nameToCode[code] = upperCode;
      nameToCode[upperCode] = upperCode;
      codeToName[upperCode] = name;
    });
    return { nameToCode, codeToName };
  };

  const normalizeCode = (value, mappingObject) => {
    if (!value) return null;
    const directMatch = mappingObject[value];
    if (directMatch) return directMatch;
    const upperValue = value.toUpperCase();
    const upperMatch = mappingObject[upperValue];
    if (upperMatch) return upperMatch;
    return upperValue;
  };

  const processMetadata = (data) => {
    // 1. Cuerpos y Servicios
    const corpsList = [];
    const corpsMetadataForMap = {};
    if (data.cuerpos_y_servicios) {
      Object.entries(data.cuerpos_y_servicios).forEach(([index, name]) => {
        const idx = parseInt(index);
        let firestoreCode, type;
        if (idx >= 0 && idx <= 3) {
          firestoreCode = RTDB_TO_FIRESTORE_CORPS[index] || `CG${index}`;
          type = 'cuerpo';
        } else {
          const serviceIdx = (idx - 4).toString();
          firestoreCode = RTDB_TO_FIRESTORE_SERVICES[serviceIdx] || `SRV${index}`;
          type = 'servicio';
        }
        const uniqueId = `${type.toUpperCase()}_${firestoreCode}`;
        corpsList.push({ code: uniqueId, originalCode: firestoreCode, name, type });
        corpsMetadataForMap[uniqueId] = name;
        corpsMetadataForMap[firestoreCode] = name;
      });
    }
    setCorpsOptions(corpsList);
    setCorpsMap(createBidirectionalMap(corpsMetadataForMap));

    // 2. Grados
    if (data.grados) {
      const hierarchies = Object.entries(data.grados).map(([index, value]) => {
        const firestoreCode = RTDB_TO_FIRESTORE_HIERARCHY[index] || `GRD${index}`;
        return { code: firestoreCode, name: value };
      });
      setHierarchyOptions(hierarchies);
      const hierarchyMetadata = {};
      Object.entries(data.grados).forEach(([index, value]) => {
        const firestoreCode = RTDB_TO_FIRESTORE_HIERARCHY[index] || `GRD${index}`;
        hierarchyMetadata[firestoreCode] = value;
      });
      setHierarchyMap(createBidirectionalMap(hierarchyMetadata));
    }

    // 3. Especialidades
    const specialtiesMap = {};
    const allSpecsSet = new Set();
    if (data.especialidades) {
      Object.entries(data.especialidades).forEach(([name, specs]) => {
        if (Array.isArray(specs)) {
          specialtiesMap[name] = specs;
          specs.forEach(spec => allSpecsSet.add(spec));
        }
      });
    }
    setSpecialtiesByCorps(specialtiesMap);

    const specialtyMetadata = {};
    const allSpecs = Array.from(allSpecsSet).map(specName => {
      const firestoreCode = SPECIALTY_NAME_TO_CODE[specName] || specName;
      specialtyMetadata[firestoreCode] = specName;
      return { code: firestoreCode, name: specName, originalName: specName };
    });
    setAllSpecialties(allSpecs);
    setSpecialtyOptions(allSpecs);
    setSpecialtyMap(createBidirectionalMap(specialtyMetadata));
  };

  const loadHierarchyData = () => {
    const inst = profileData?.institucion || 'SEMAR';
    const prefix = getRTDBPrefix(inst);

    const paths = {
      areas: prefix ? `${prefix}/areas` : 'areas',
      temas: prefix ? `${prefix}/temas` : 'temas',
      metadata: 'app_metadata'
    };

    const refs = {
      areas: ref(database, paths.areas),
      temas: ref(database, paths.temas),
      metadata: ref(database, paths.metadata)
    };

    onValue(refs.areas, snap => setAreas(snap.exists() ? Object.values(snap.val()) : []));
    onValue(refs.temas, snap => setTemas(snap.exists() ? Object.values(snap.val()) : []));
    onValue(refs.metadata, snap => {
      if (snap.exists()) {
        const data = snap.val();
        setAppMetadata(data);
        processMetadata(data);
      }
    });
  };

  // Carga SOLO los temas de la especialidad seleccionada bajo demanda.
  // Antes se descargaba el árbol completo `especialidades_temas` (~42 MB).
  const [specialtyTemasLoading, setSpecialtyTemasLoading] = useState(false);
  const loadSpecialtyTemas = useCallback(async (jerarquia, cuerpo, especialidad) => {
    if (!jerarquia || !cuerpo || !especialidad) {
      setSpecialtyTemas({});
      return;
    }
    const inst = profileData?.institucion || 'SEMAR';
    const prefix = getRTDBPrefix(inst);
    const path = prefix
      ? `${prefix}/especialidades_temas/${jerarquia}/${cuerpo}/${especialidad}/temas`
      : `especialidades_temas/${jerarquia}/${cuerpo}/${especialidad}/temas`;
    setSpecialtyTemasLoading(true);
    try {
      const snap = await get(ref(database, path));
      const temas = snap.exists() ? snap.val() : {};
      setSpecialtyTemas({ [jerarquia]: { [cuerpo]: { [especialidad]: { temas } } } });
    } catch (err) {
      console.warn('[VortexDocs] ⚠️ Error cargando temas de especialidad:', err);
      setSpecialtyTemas({});
    } finally {
      setSpecialtyTemasLoading(false);
    }
  }, [profileData?.institucion]);

  useEffect(() => {
    if (mode === 'specialty') {
      loadSpecialtyTemas(specialtyJerarquia, specialtyCuerpo, specialtyEspecialidad);
    }
  }, [mode, specialtyJerarquia, specialtyCuerpo, specialtyEspecialidad, loadSpecialtyTemas]);

  // Filter specialties when corps changes (Universal)
  useEffect(() => {
    if (!specialtyCuerpo) {
      setSpecialtyOptions(allSpecialties);
    } else {
      const corpsName = corpsMap.codeToName[specialtyCuerpo] || specialtyCuerpo;
      const corpsSpecialties = specialtiesByCorps[corpsName] || [];
      const filteredSpecs = allSpecialties.filter(spec => corpsSpecialties.includes(spec.originalName || spec.name));
      setSpecialtyOptions(filteredSpecs);
    }
  }, [specialtyCuerpo, allSpecialties, specialtiesByCorps, corpsMap]);

  const loadModels = async () => {
    setIsLoadingModels(true);
    setModelLoadError('');
    try {
      console.log('[VortexDocs] 📊 Iniciando carga de modelos de IA...');
      await vortexDocsService.initializeService();
      const models = vortexDocsService.getAvailableModels();
      const currentModel = vortexDocsService.getCurrentModel();

      setAvailableModels(models);
      setSelectedModel(currentModel?.key || '');
      setCurrentModelInfo(currentModel);

      console.log('[VortexDocs] ✅ Modelos cargados:', Object.keys(models));
      console.log('[VortexDocs] 🤖 Modelo actual:', currentModel);
    } catch (error) {
      console.error('[VortexDocs] ❌ Error cargando modelos:', error);
      setModelLoadError('Error cargando modelos de IA: ' + error.message);
    } finally {
      setIsLoadingModels(false);
    }
  };


  const handleModelChange = (modelKey) => {
    const success = vortexDocsService.setModel(modelKey);
    if (success) {
      setSelectedModel(modelKey);
      setCurrentModelInfo(vortexDocsService.getCurrentModel());
      setSuccess(`✅ Modelo de IA cambiado a: ${availableModels[modelKey]?.displayName || modelKey}`);
      setError(''); // Limpiar errores anteriores
    } else {
      setError('❌ Error al cambiar el modelo de IA');
    }
  };

  // Función para verificar si hay un modelo seleccionado
  const isModelSelected = () => {
    return selectedModel === 'gemini-2.5-flash-lite' || (selectedModel && availableModels[selectedModel]);
  };

  const extractTextFromFile = async (file) => {
    if (!file) return;

    setIsLoading(true);
    setError('');
    setSuccess('');

    try {
      const fileType = file.name.split('.').pop().toLowerCase();
      console.log(`[VortexDocs] 📄 Iniciando extracción de texto de: ${file.name} (Tipo: ${fileType})`);
      let text = '';

      if (fileType === 'pdf') {
        text = await pdfToText(file);
      } else if (fileType === 'docx') {
        const arrayBuffer = await file.arrayBuffer();
        const result = await mammoth.extractRawText({ arrayBuffer: arrayBuffer });
        text = result.value;
      } else if (fileType === 'txt') {
        text = await file.text();
      } else if (['png', 'jpg', 'jpeg'].includes(fileType)) {
        text = await vortexDocsService.extractTextFromFile(file);
      } else {
        throw new Error('Tipo de archivo no soportado. Usa PDF, DOCX, TXT o Imagen.');
      }

      if (!text.trim()) {
        throw new Error('No se pudo extraer texto del archivo.');
      }

      const cleanedText = text.replace(/\n\s*\n/g, '\n\n').trim();
      console.log(`[VortexDocs] ✅ Texto extraído con éxito. Longitud: ${cleanedText.length} caracteres.`);
      setExtractedText(cleanedText);
      setSuccess('Texto extraído exitosamente');
    } catch (err) {
      console.error(`[VortexDocs] ❌ Error en extracción:`, err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setFileName(file.name);
      extractTextFromFile(file);
    }
  };

  const delay = (ms) => new Promise(res => setTimeout(res, ms));

  // Preguntas recomendadas según la longitud del texto extraído
  const recommendedQuestions = useMemo(
    () => estimateQuestionsForText(extractedText?.length || 0),
    [extractedText]
  );

  const fetchQuestionsFromAI = async (text, count, diff, modelConfig, apiKey, previousQuestions = [], extraOptions = {}) => {
    const inst = profileData?.institucion || 'SEMAR';
    const institutionPrompt = INSTITUTION_PROMPTS[inst] || INSTITUTION_PROMPTS['SEMAR'];
    return await vortexDocsService.generateQuestions(
      text, count, diff,
      modelConfig.key || selectedModel,
      auth.currentUser?.uid,
      previousQuestions,
      institutionPrompt,
      extraOptions
    );
  };

  const validateQuestions = (questions) => {
    if (!Array.isArray(questions)) return [];
    return questions.map(q => {
      // Normalizar campos (la IA a veces usa nombres distintos)
      const questionText = q.question || q.pregunta || q.text || '';
      const options = q.options || q.respuestas || q.opciones || [];
      let explanation = q.explanation || q.explicacion || '';
      
      // Intentar encontrar el índice de la respuesta correcta
      let correctIdx = -1;
      const possibleCorrectValue = q.correctAnswer !== undefined ? q.correctAnswer : 
                                  (q.respuesta_correcta !== undefined ? q.respuesta_correcta : 
                                  (q.correct_index !== undefined ? q.correct_index : -1));

      if (typeof possibleCorrectValue === 'number') {
        correctIdx = possibleCorrectValue;
      } else if (typeof possibleCorrectValue === 'string') {
        const parsed = parseInt(possibleCorrectValue);
        if (!isNaN(parsed)) {
          correctIdx = parsed;
        } else {
          correctIdx = options.findIndex(opt => 
            opt === possibleCorrectValue || 
            (typeof opt === 'object' && opt.respuesta === possibleCorrectValue)
          );
        }
      }

      const normalizedOptions = options.map(opt => {
        let text = '';
        if (typeof opt === 'object' && opt !== null) {
          text = opt.respuesta || opt.texto || String(opt);
        } else {
          text = String(opt);
        }
        // Quitar prefijos tipo "A)", "B)", "1.", "a)", etc. que el modelo pueda incluir
        return text.replace(/^([A-Da-d]\)|[1-4][).])\s*/, '').trim();
      });

      return {
        id: q.id || Math.floor(Math.random() * 10000),
        question: questionText,
        options: normalizedOptions,
        correctAnswer: Number(correctIdx),
        explanation: explanation,
        difficulty: q.difficulty || diff || 'medium'
      };
    }).filter(q =>
      q.question && q.question.length > 5 &&
      Array.isArray(q.options) &&
      q.options.length === 4 &&
      q.options.every(opt => opt && opt.trim() !== '') &&
      q.correctAnswer >= 0 &&
      q.correctAnswer <= 3
    );
  };

  const saveToAdminCache = async (questions) => {
    try {
      await set(ref(database, 'admin/last_generated_quiz'), {
        id: `Q${Date.now() % 10000}`,
        titulo: `Generado: ${new Date().toLocaleDateString()}`,
        preguntas: questions.slice(0, 500), // Aumentado a 500 para permitir cuestionarios largos
        timestamp: rtdbTimestamp()
      });
      console.log('[VortexDocs] 💾 Caché de admin actualizada.');
    } catch (cacheErr) {
      console.warn('[VortexDocs] ⚠️ No se pudo guardar en la caché de admin:', cacheErr);
    }
  };

  const generateQuiz = async () => {
    if (!extractedText.trim()) return setError('Primero extrae texto');
    if (!isModelSelected()) return setError('Selecciona un modelo');

    setGeneratedQuestions([]);
    setGenerating(true);
    setError('');
    setSuccess('');
    setGenerationStep('Iniciando...');
    setGenerationProgress(5);

    const modelConfig = availableModels[selectedModel] || { key: selectedModel, modelName: selectedModel };
    const apiKey = modelConfig?.apiKey;
    const isLocalModel = modelConfig.provider === 'lmstudio' || selectedModel.startsWith('local_') || selectedModel === 'lm-studio-local';
    const questionsToGenerate = parseInt(numQuestions) || 10;

    try {
      console.log(`[VortexDocs] ✨ Iniciando generación (${isLocalModel ? 'Modo Local' : 'Cloud'}) para ${questionsToGenerate} preguntas...`);

      const allValidQuestions = [];
      const textChunks = [];

      if (isLocalModel) {
        // Para modelos locales: más chunks con menos preguntas cada uno
        // Objetivo: 5-10 preguntas por chunk para mejor calidad
        const targetChunks = Math.min(8, Math.max(2, Math.ceil(questionsToGenerate / 8)));
        const dynamicChunkSize = Math.min(8000, Math.max(3000, Math.floor(extractedText.length / targetChunks)));
        
        for (let i = 0; i < targetChunks; i++) {
          const start = Math.floor(i * (extractedText.length / targetChunks));
          textChunks.push(extractedText.substring(start, start + dynamicChunkSize));
        }
      } else {
        const chunkSize = 20000;
        for (let i = 0; i < extractedText.length; i += chunkSize) {
          textChunks.push(extractedText.substring(i, i + chunkSize));
        }
      }

      const numChunks = textChunks.length;
      const questionsPerChunk = textChunks.map((_, i) => {
        const base = Math.floor(questionsToGenerate / numChunks);
        const remainder = questionsToGenerate % numChunks;
        return base + (i < remainder ? 1 : 0);
      });

      console.log(`[VortexDocs] 📊 Distribución proporcional de preguntas (${numChunks} partes):`, questionsPerChunk);

      const isMassive = !isLocalModel && (questionsToGenerate >= 200 || extractedText.length >= 100000);
      if (isMassive) {
        console.log(`[VortexDocs] ⚡ Modo Caché de Contexto activado (Documento: ${extractedText.length.toLocaleString()} chars, Objetivo: ${questionsToGenerate} preguntas).`);
      }

      // Rastrear éxito de cada chunk para reintentos inteligentes
      const chunkSuccess = new Array(numChunks).fill(0);

      // Procesar cada chunk
      for (let i = 0; i < numChunks; i++) {
        if (questionsPerChunk[i] <= 0) continue;

        // Para modelos cloud en documentos gigantesco (>120k chars), se usa una ventana de contexto envolvente
        // Para modelos locales ya usamos textChunks[] directamente desde la división inicial
        let contextText = textChunks[i];

        const extraOptions = isMassive ? {
          fullDocumentContext: contextText,
          sectionTarget: `Parte ${i + 1} de ${numChunks}`,
          isMassiveGeneration: true
        } : {};

        console.info(`[VortexDocs] 📤 Enviando Chunk ${i + 1}/${numChunks} (${textChunks[i].length} caracteres) ${isMassive ? '[⚡ Context Caching]' : ''} para generar ${questionsPerChunk[i]} preguntas...`);
        
        setGenerationStep(isMassive
          ? `⚡ [Caché de Contexto] Procesando parte ${i + 1} de ${numChunks} (${allValidQuestions.length}/${questionsToGenerate} preguntas)...`
          : `Procesando parte ${i + 1} de ${numChunks} (${allValidQuestions.length}/${questionsToGenerate} preguntas)...`
        );
        setGenerationProgress(Math.round(10 + (70 * (i / numChunks))));

        const currentMaxTokens = questionsPerChunk[i] <= 10 ? 1500 :
          questionsPerChunk[i] <= 20 ? 2500 :
            questionsPerChunk[i] <= 30 ? 3500 : 4000;

        try {
          const questions = await fetchQuestionsFromAI(
            textChunks[i],
            questionsPerChunk[i],
            difficulty,
            { ...modelConfig, maxTokens: currentMaxTokens },
            apiKey,
            allValidQuestions.map(q => q.question),
            extraOptions
          );

          const validFromChunk = validateQuestions(questions);
          // Evitar duplicados exactos por texto de pregunta
          const uniqueFromChunk = validFromChunk.filter(q => !allValidQuestions.some(aq => aq.question === q.question));
          allValidQuestions.push(...uniqueFromChunk);
          
          // Rastrear éxito del chunk
          chunkSuccess[i] = uniqueFromChunk.length;

          console.log(`[VortexDocs] 📥 Recibidas ${uniqueFromChunk.length} preguntas únicas de la parte ${i + 1}. Total acumulado: ${allValidQuestions.length}`);
        } catch (chunkErr) {
          console.warn(`[VortexDocs] ⚠️ Error o rate limit en parte ${i + 1}/${numChunks}: ${chunkErr.message}`);
          if (chunkErr.message.includes('LM Studio') || chunkErr.message.includes('127.0.0.1:1234') || chunkErr.message.includes('memoria RAM/VRAM')) {
            throw chunkErr;
          }
        }

        if (i < numChunks - 1 && numChunks > 1) {
          // Para modelos locales no necesitamos demoras largas de rate limit de Google
          const isLocal = modelConfig.provider === 'lmstudio' || selectedModel === 'lm-studio-local';
          const chunkDelay = isLocal ? 300 : (isMassive ? (6000 + Math.random() * 2500) : (3500 + Math.random() * 2500));
          if (!isLocal) {
            console.log(`[VortexDocs] ⏳ Esperando ${Math.round(chunkDelay)}ms para evitar rate limit...`);
          }
          await delay(chunkDelay);
        }
      }

      // Reintentos agresivos si faltan
      let missing = questionsToGenerate - allValidQuestions.length;
      let retries = 0;
      const maxRetries = 10;
      
      // Rastrear reintentos por chunk para balancear
      const chunkRetries = new Array(numChunks).fill(0);

      while (missing > 0 && retries < maxRetries) {
        retries++;
        // Para modelos locales, solicitar más preguntas por reintento (tokens baratos)
        const maxPerRetry = isLocalModel ? 20 : 10;
        const questionsInRetry = Math.min(missing, maxPerRetry);

        console.log(`[VortexDocs] ⚠️ Faltan ${missing} preguntas. Reintento ${retries}/${maxRetries} solicitando lote de ${questionsInRetry}...`);
        setGenerationStep(isMassive
          ? `⚡ [Caché de Contexto] Reintento ${retries}: Completando ${missing} preguntas faltantes (${allValidQuestions.length}/${questionsToGenerate})...`
          : `Reintento ${retries}: Completando ${missing} preguntas faltantes (${allValidQuestions.length}/${questionsToGenerate})...`
        );

        // Elegir chunk inteligentemente: priorizar chunks que produjeron más preguntas únicas
        // y que no se hayan reintentado demasiado
        const suitableChunks = textChunks
          .map((c, idx) => ({ 
            chunk: c, 
            idx, 
            length: c.length,
            success: chunkSuccess[idx] || 0,
            retries: chunkRetries[idx] || 0
          }))
          .filter(c => c.length > 2000)
          // Ordenar por: éxito descendente, luego por menos reintentos
          .sort((a, b) => {
            if (b.success !== a.success) return b.success - a.success;
            return a.retries - b.retries;
          });

        if (suitableChunks.length === 0) break;
        
        // Usar el chunk con mejor ratio éxito/reintentos
        const chunkInfo = suitableChunks[0];
        chunkRetries[chunkInfo.idx]++;  // Incrementar contador persistente
        const randomChunk = chunkInfo.chunk;

        // Para modelos locales, usar más tokens en reintentos
        const retryMaxTokens = isLocalModel 
          ? (questionsInRetry <= 10 ? 3000 : 4000)
          : (questionsInRetry <= 5 ? 2000 : 3500);

        const retryExtraOptions = isMassive ? {
          fullDocumentContext: extractedText,
          sectionTarget: `Reintento ${retries}`,
          isMassiveGeneration: true
        } : {};

        try {
          const extraQuestions = await fetchQuestionsFromAI(
            randomChunk,
            questionsInRetry,
            difficulty,
            { ...modelConfig, maxTokens: retryMaxTokens },
            apiKey,
            allValidQuestions.map(q => q.question),
            retryExtraOptions
          );

          const validFromRetry = validateQuestions(extraQuestions).filter(q =>
            !allValidQuestions.some(aq => aq.question.toLowerCase().trim() === q.question.toLowerCase().trim())
          );

          if (validFromRetry.length > 0) {
            allValidQuestions.push(...validFromRetry);
            console.log(`[VortexDocs] ✅ Recuperadas ${validFromRetry.length} preguntas nuevas en reintento ${retries}. Total: ${allValidQuestions.length}`);
          } else {
            console.warn(`[VortexDocs] ⚠️ El reintento ${retries} no aportó preguntas nuevas.`);
          }
        } catch (retryErr) {
          console.warn(`[VortexDocs] ⚠️ Error en reintento ${retries}: ${retryErr.message}`);
        }

        missing = questionsToGenerate - allValidQuestions.length;

        if (missing > 0) {
          const retryDelay = 3000 + Math.random() * 2000;
          console.log(`[VortexDocs] ⏳ Pausa de ${Math.round(retryDelay)}ms antes del siguiente reintento...`);
          await delay(retryDelay);
        }
      }

      if (allValidQuestions.length === 0) {
        throw new Error("No se pudo generar ninguna pregunta válida. Revisa la configuración del modelo o el texto.");
      }

      setGeneratedQuestions(allValidQuestions);

      const successPercent = Math.round((allValidQuestions.length / questionsToGenerate) * 100);
      if (allValidQuestions.length < questionsToGenerate) {
        setSuccess(`⚠️ Generación parcial: ${allValidQuestions.length} de ${questionsToGenerate} (${successPercent}%)`);
        console.warn(`[VortexDocs] ⚠️ Cuestionario incompleto (${allValidQuestions.length}/${questionsToGenerate}).`);
      } else {
        setSuccess(`✅ Generadas ${allValidQuestions.length} preguntas interactivas`);
      }
      setGenerationProgress(100);
      saveToAdminCache(allValidQuestions);

    } catch (err) {
      console.error('[VortexDocs] ❌ Error en generación de Quiz:', err);
      setError(`Error: ${err.message}`);
    } finally {
      setGenerating(false);
      setGenerationStep('');
    }
  };

  const handleSaveToAccount = async () => {
    if (!generatedQuestions.length) return;
    setIsLoading(true);
    setSaveProgress(0);

    try {
      console.log('[VortexDocs] 📂 Guardando cuestionario en cuenta de usuario...');
      const temaName = mode === 'general'
        ? (temas.find(t => t.id === parseInt(selectedGeneralTemaId))?.nombre || 'General')
        : (specialtyTemas[specialtyJerarquia]?.[specialtyCuerpo]?.[specialtyEspecialidad]?.temas?.[specialtyTemaId]?.nombre || 'Especialidad');

      const quizData = {
        name: `Docs: ${temaName}`,
        userId: auth.currentUser.uid,
        userName: auth.currentUser.displayName || 'Usuario',
        sourceType: 'document',
        sourceName: fileName,
        areaId: mode === 'general' ? selectedAreaId : '3',
        temaId: mode === 'general' ? 999 : specialtyTemaId,
        temaName: temaName,
        questionCount: generatedQuestions.length,
        difficulty,
        createdAt: firestoreTimestamp(),
        averageScore: 0,
        completionCount: 0
      };

      const quizRef = await addDoc(collection(db, 'user_quizzes', auth.currentUser.uid, 'quizzes'), quizData);
      console.log(`[VortexDocs] 📄 Quiz creado en Firestore: ${quizRef.id}`);
      const questionsRef = collection(db, 'user_quizzes', auth.currentUser.uid, 'quizzes', quizRef.id, 'questions');

      for (let i = 0; i < generatedQuestions.length; i++) {
        const q = generatedQuestions[i];
        const respuestasMap = {};
        q.options.forEach((opt, idx) => {
          respuestasMap[1000 + idx] = {
            id: 1000 + idx,
            respuesta: opt,
            correcta: idx === q.correctAnswer
          };
        });

        await addDoc(questionsRef, {
          pregunta: q.question || 'Sin pregunta',
          respuestas: respuestasMap,
          respuesta_correcta: q.options[q.correctAnswer] || q.options[0] || 'Sin respuesta',
          explicacion: q.explanation || '',
          addedAt: firestoreTimestamp()
        });
        setSaveProgress(Math.round(((i + 1) / generatedQuestions.length) * 100));
        if ((i + 1) % 5 === 0) console.log(`[VortexDocs] 📝 Guardadas ${i + 1}/${generatedQuestions.length} preguntas en tu cuenta...`);
      }

      invalidateStudyQuestionsCache();
      console.log('[VortexDocs] ✅ Guardado en cuenta finalizado con éxito.');

      // Deducir tokens (Basado en Realtime Database licencesPremium)
      try {
        if (auth.currentUser && auth.currentUser.email) {
          const tokenCost = Math.ceil(extractedText.length * 0.1 + generatedQuestions.length * 1000);
          const encodeEmail = (email) => email.replace(/\./g, '_dot_').replace(/@/g, '_at_');
          const encodedEmail = encodeEmail(auth.currentUser.email);
          const tokenRef = ref(database, `licencesPremium/${encodedEmail}/tokensUsed`);
          
          await runTransaction(tokenRef, (current) => {
            return (current || 0) + tokenCost;
          });
          console.log(`[VortexDocs] 💳 Tokens usados: ${tokenCost} en RTDB para ${encodedEmail}`);
        }
      } catch (tokenErr) {
        console.warn('[VortexDocs] ⚠️ Error deduciendo tokens en RTDB:', tokenErr);
      }

      setSuccess('Cuestionario guardado en tu cuenta');
    } catch (err) {
      console.error('[VortexDocs] ❌ Error al guardar en cuenta:', err);
      setError('Error al guardar: ' + err.message);
    } finally {
      setIsLoading(false);
      setSaveProgress(0);
    }
  };

  const handlePublishSpecialtyQuizz = async () => {
    if (mode !== 'specialty' || !specialtyJerarquia || !specialtyCuerpo || !specialtyEspecialidad || !specialtyTemaId) {
      return setError('Configura todos los campos de especialidad');
    }

    setIsLoading(true);
    setPublishProgress(0);

    try {
      console.log('[VortexDocs] 🎗️ Publicando Cuestionario Oficial en RTDB...');
      const temaObj = specialtyTemas[specialtyJerarquia][specialtyCuerpo][specialtyEspecialidad].temas[specialtyTemaId];
      const quizzIdNumber = Date.now() % 10000; // Un ID numérico corto
      const quizzId = `Q${quizzIdNumber}`;
      // Normalización robusta para paridad con Android
      const formattedQuestions = generatedQuestions.map((q, qIdx) => {
        const qId = qIdx + 1; // ID secuencial para evitar sobrescrituras
        const rawRespuestas = q.respuestas || q.options || [];
        return {
          id: qId,
          pregunta: q.pregunta || q.question || 'Sin pregunta',
          respuestas: rawRespuestas.map((opt, optIdx) => {
            const rId = (qId * 10) + optIdx;
            const isString = typeof opt === 'string';
            
            let isCorrect = false;
            if (isString) {
                isCorrect = (Number(optIdx) === Number(q.correctAnswer)) || (opt === q.respuesta) || (opt === q.respuesta_correcta);
            } else {
                isCorrect = opt.correcta === true || opt.correcta === 1 || String(opt.correcta) === "1" || String(opt.correcta).toLowerCase() === "true";
            }
            
            return {
              id: rId,
              respuesta: isString ? opt : (opt.respuesta || opt.texto || 'Sin respuesta'),
              correcta: isCorrect
            };
          }),
          explicacion: q.explicacion || q.explanation || ''
        };
      });
      const inst = profileData?.institucion || 'SEMAR';
      const prefix = getRTDBPrefix(inst);
      const basePath = prefix 
        ? `${prefix}/especialidades_temas/${specialtyJerarquia}/${specialtyCuerpo}/${specialtyEspecialidad}/temas/${specialtyTemaId}/cuestionarios/${quizzIdNumber}`
        : `especialidades_temas/${specialtyJerarquia}/${specialtyCuerpo}/${specialtyEspecialidad}/temas/${specialtyTemaId}/cuestionarios/${quizzIdNumber}`;

      console.log(`[VortexDocs] 🛠️ Ruta RTDB: ${basePath}`);

      await set(ref(database, basePath), {
        id: quizzIdNumber,
        titulo: `Docs: ${temaObj.nombre}`,
        preguntas: formattedQuestions,
        publicado: rtdbTimestamp()
      });

      console.log('[VortexDocs] ✅ Publicación oficial completada.');
      setSuccess('¡Cuestionario oficial publicado exitosamente!');
    } catch (err) {
      console.error('[VortexDocs] ❌ Error en publicación oficial:', err);
      setError('Error al publicar: ' + err.message);
    } finally {
      setIsLoading(false);
      setPublishProgress(0);
    }
  };

  const handlePublishToPreguntasPorTema = async () => {
    if (!generatedQuestions.length) return setError('Primero genera preguntas');
    if (!selectedGeneralTemaId) return setError('Selecciona un tema destino');
    if (mode !== 'general') return setError('Debes estar en modo general');

    setIsLoading(true);
    setPublishProgress(0);
    try {
      const temaId = parseInt(selectedGeneralTemaId);
      const temaObj = temas.find(t => t.id === temaId);
      const questionsUpdate = {};
      generatedQuestions.forEach((q, qIdx) => {
        const qId = qIdx + 1;
        const rawResp = q.respuestas || q.options || [];
        const formattedRespuestas = {};
        rawResp.forEach((opt, optIdx) => {
          const rId = (qId * 10) + optIdx;
          const isStr = typeof opt === 'string';
          let isCorrect = false;
          if (isStr) isCorrect = Number(optIdx) === Number(q.correctAnswer) || opt === q.respuesta || opt === q.respuesta_correcta;
          else isCorrect = opt.correcta === true || opt.correcta === 1;
          formattedRespuestas[rId] = {
            id: rId,
            respuesta: isStr ? opt : (opt.respuesta || opt.texto || ''),
            correcta: isCorrect
          };
        });
        questionsUpdate[qId] = {
          id: qId,
          temaId: temaId,
          tema_id: temaId,
          pregunta: q.pregunta || q.question || 'Sin pregunta',
          respuestas: formattedRespuestas,
          explicacion: q.explicacion || q.explanation || ''
        };
      });

      await set(ref(database, `preguntas_por_tema/${temaId}`), questionsUpdate);
      setSuccess(`✅ Publicado en preguntas_por_tema/${temaId} con ${generatedQuestions.length} preguntas`);
    } catch (err) {
      setError('Error al publicar: ' + err.message);
    } finally {
      setIsLoading(false);
      setPublishProgress(0);
    }
  };

  const handlePublishToGlobalVault = async () => {
    if (!generatedQuestions.length) return;

    const temaGeneral = temas.find(t => t.id === parseInt(selectedGeneralTemaId));
    const defaultTitle = fileName ? `Quiz de ${fileName}` : (temaGeneral?.nombre || 'Nuevo Cuestionario');
    const title = window.prompt("Ingrese el título para la Bóveda Compartida (Será público y anónimo):", defaultTitle);

    if (!title) return;

    setIsGlobalVaultPublishing(true);
    try {
      console.log('[VortexDocs] ☁️ Publicando en Bóveda Compartida Global...');

      await addDoc(collection(db, 'shared_vault'), {
        titulo: title,
        preguntas: generatedQuestions,
        fecha_creacion: firestoreTimestamp(),
        tipo: 'general',
        autor: 'anonimo'
      });

      console.log('[VortexDocs] ✅ Publicación en Bóveda Global finalizada.');
      setSuccess('✅ Publicado exitosamente en la Bóveda Compartida (Anónima).');
    } catch (err) {
      console.error('[VortexDocs] ❌ Error en publicación Global:', err);
      setError('Error al publicar en Bóveda Global: ' + err.message);
    } finally {
      setIsGlobalVaultPublishing(false);
    }
  };

  const handlePublishToAcademy = async () => {
    if (!generatedQuestions.length) return setError('Primero genera preguntas');
    if (!specialtyJerarquia || !specialtyCuerpo || !specialtyEspecialidad) {
      return setError('Configura jerarquía, cuerpo/servicio y especialidad para Vortex Academy');
    }

    const temaObj = specialtyTemas[specialtyJerarquia]?.[specialtyCuerpo]?.[specialtyEspecialidad]?.temas?.[specialtyTemaId];
    const defaultTitle = fileName ? `Cuestionario de ${fileName}` : (temaObj?.nombre || 'Cuestionario Vortex Academy');
    const title = window.prompt('Título del cuestionario para Vortex Academy:', defaultTitle);
    if (!title) return;

    setIsVaultPublishing(true);
    setPublishProgress(0);
    try {
      console.log('[VortexDocs] 🎓 Publicando en Vortex Academy (RTDB)...');
      const quiz = {
        id: `qz_${Date.now()}`,
        title,
        type: 'quiz',
        questions: generatedQuestions.map((q, qIdx) => ({
          id: qIdx + 1,
          question: q.question || q.pregunta || 'Sin pregunta',
          options: (q.options || []).map((opt) => (typeof opt === 'string' ? opt : (opt.respuesta || opt.texto || ''))),
          correct_index: Number(q.correctAnswer) || 0,
          explanation: q.explanation || q.explicacion || '',
        })),
      };
      await saveAcademyQuiz(
        { jerarquia: specialtyJerarquia, cuerpo: specialtyCuerpo, especialidad: specialtyEspecialidad },
        quiz
      );
      console.log('[VortexDocs] ✅ Publicación en Vortex Academy finalizada.');
      setSuccess(`🎓 Publicado en Vortex Academy: ${specialtyJerarquia} / ${specialtyCuerpo} / ${specialtyEspecialidad}`);
    } catch (err) {
      console.error('[VortexDocs] ❌ Error publicando en Vortex Academy:', err);
      setError('Error al publicar en Vortex Academy: ' + err.message);
    } finally {
      setIsVaultPublishing(false);
      setPublishProgress(0);
    }
  };

  const handleSaveToVault = async () => {
    if (!generatedQuestions.length) return;
    setIsVaultPublishing(true);

    try {
      console.log('[VortexDocs] 🛡️ Publicando en Bóveda Técnica (Firestore)...');

      // Usar mapeos dinámicos en lugar de funciones hardcoded
      const hierarchyCode = normalizeCode(specialtyJerarquia, hierarchyMap.nameToCode) || 'MRO';
      const corpsCode = normalizeCode(specialtyCuerpo, corpsMap.nameToCode) || 'SAIN';
      const specialtyCode = normalizeCode(specialtyEspecialidad, specialtyMap.nameToCode) || 'OFTA';

      const themeCode = prompt('Ingresa un código corto para el tema (ej: NAVEGACION, EXCEL, LEYES):', (mode === 'general' ? temas.find(t => t.id === parseInt(selectedGeneralTemaId))?.nombre : '')?.toUpperCase() || '');
      if (!themeCode) return;

      const standardizedId = `${hierarchyCode}.${corpsCode}.${specialtyCode}.${themeCode.toUpperCase().replace(/\s/g, '_')}_${auth.currentUser.displayName?.replace(/\s/g, '_') || 'USUARIO'}`;

      const areaId = mode === 'specialty' ? '3' : selectedAreaId;

      const vaultData = {
        name: `Docs: ${mode === 'general' ? (temas.find(t => t.id === parseInt(selectedGeneralTemaId))?.nombre || 'General') : specialtyTemaId}`,
        standardizedId,
        hierarchyCode,
        corpsCode,
        specialtyCode,
        areaId: areaId || '1',
        themeCode: themeCode.toUpperCase(),
        questionCount: generatedQuestions.length,
        difficulty,
        authorName: auth.currentUser?.displayName || 'Vortex Admin',
        authorId: auth.currentUser?.uid,
        timestamp: firestoreTimestamp(),
        source: 'VortexDocs',
        isVault: true,
        tipo: mode === 'general' ? 'general' : 'tecnica'
      };

      const vaultRef = await addDoc(collection(db, 'shared_vault', auth.currentUser.uid, 'quizzes'), vaultData);
      console.log(`[VortexDocs] 🏰 Entrada en Bóveda creada: ${vaultRef.id} (${hierarchyCode}.${corpsCode}.${specialtyCode})`);
      const questionsRef = collection(db, 'shared_vault', auth.currentUser.uid, 'quizzes', vaultRef.id, 'questions');

      for (let i = 0; i < generatedQuestions.length; i++) {
        const q = generatedQuestions[i];
        const respuestasMap = {};
        q.options.forEach((opt, idx) => {
          const isCorrect = Number(idx) === Number(q.correctAnswer);
          respuestasMap[1000 + idx] = {
            id: 1000 + idx,
            respuesta: opt,
            correcta: isCorrect // Guardamos como booleano true/false
          };
        });
        await addDoc(questionsRef, {
          pregunta: q.question || 'Sin pregunta',
          respuestas: respuestasMap,
          respuesta_correcta: q.options[q.correctAnswer] || q.options[0] || 'Sin respuesta',
          explicacion: q.explanation || '',
          addedAt: firestoreTimestamp()
        });
        if ((i + 1) % 5 === 0) console.log(`[VortexDocs] 🚢 Publicando en Bóveda: ${i + 1}/${generatedQuestions.length} preguntas...`);
      }

      console.log('[VortexDocs] ✅ Publicación en Bóveda finalizada.');
      setSuccess(`¡Publicado en la Bóveda Técnica como ${hierarchyCode}.${corpsCode}.${specialtyCode}!`);
    } catch (err) {
      console.error('[VortexDocs] ❌ Error en publicación de Bóveda:', err);
      setError(err.message);
    } finally {
      setIsVaultPublishing(false);
    }
  };

  const clearAll = () => {
    setExtractedText('');
    setSummary('');
    setGeneratedQuestions([]);
    setSelectedFile(null);
    setFileName('');
    setError('');
    setSuccess('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div style={{
      maxWidth: '1200px',
      margin: '0 auto',
      padding: 'var(--spacing-lg)',
      background: 'var(--bg-primary)',
      color: 'var(--text-primary)',
      fontFamily: 'var(--font-primary)'
    }}>
      <div style={{
        textAlign: 'center',
        marginBottom: 'var(--spacing-xl)'
      }}>
        <h1 style={{
          fontSize: '2.5rem',
          fontWeight: 'bold',
          color: 'var(--accent)',
          marginBottom: 'var(--spacing-md)'
        }}>
          📄 Vortex Docs
        </h1>
        <p style={{
          fontSize: '1.1rem',
          color: 'var(--text-secondary)',
          maxWidth: '600px',
          margin: '0 auto',
          marginBottom: 'var(--spacing-lg)'
        }}>
          Extrae texto de documentos, genera resúmenes y crea cuestionarios automáticamente
        </p>

        {/* Selector de Modelo de IA */}
        <div style={{
          background: 'var(--bg-secondary)',
          borderRadius: '12px',
          padding: 'var(--spacing-lg)',
          marginBottom: 'var(--spacing-lg)',
          border: '1px solid var(--border)',
          maxWidth: '600px',
          margin: '0 auto'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 'var(--spacing-md)'
          }}>
            <h3 style={{
              fontSize: '1.2rem',
              fontWeight: 'bold',
              color: 'var(--text-primary)',
              margin: 0
            }}>
              🤖 Modelo de IA
            </h3>

            <button
              onClick={loadModels}
              disabled={isLoadingModels}
              style={{
                background: 'var(--accent)',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                padding: 'var(--spacing-sm) var(--spacing-md)',
                cursor: isLoadingModels ? 'not-allowed' : 'pointer',
                fontSize: '0.8rem',
                opacity: isLoadingModels ? 0.6 : 1,
                transition: 'transform 0.25s ease, box-shadow 0.25s ease, background-color 0.25s ease, border-color 0.25s ease'
              }}
              onMouseEnter={(e) => {
                if (!isLoadingModels) {
                  e.target.style.background = 'var(--accent-hover)';
                }
              }}
              onMouseLeave={(e) => {
                if (!isLoadingModels) {
                  e.target.style.background = 'var(--accent)';
                }
              }}
            >
              {isLoadingModels ? '⏳' : '🔄'} Recargar
            </button>
          </div>

          {isLoadingModels ? (
            <div style={{
              textAlign: 'center',
              padding: 'var(--spacing-md)'
            }}>
              <p style={{
                color: 'var(--text-secondary)',
                fontStyle: 'italic',
                margin: 0
              }}>
                ⏳ Cargando modelos de IA...
              </p>
            </div>
          ) : modelLoadError ? (
            <div style={{
              background: 'rgba(239,68,68,0.12)',
              color: 'var(--error-color, #fca5a5)',
              padding: 'var(--spacing-md)',
              borderRadius: '8px',
              border: '1px solid rgba(239,68,68,0.3)',
              marginBottom: 'var(--spacing-md)'
            }}>
              ❌ {modelLoadError}
            </div>
          ) : Object.keys(availableModels).length > 0 ? (
            <div>
              <div style={{
                display: 'flex',
                gap: 'var(--spacing-sm)',
                flexWrap: 'wrap',
                justifyContent: 'center',
                marginBottom: 'var(--spacing-md)'
              }}>
                {/* Botón Vertex AI Flash Lite (Fijo y destacado) */}
                <button
                  onClick={() => handleModelChange('gemini-2.5-flash-lite')}
                  style={{
                    background: selectedModel === 'gemini-2.5-flash-lite' ? 'var(--accent)' : '#6200ea',
                    color: 'white',
                    border: '1px solid var(--border)',
                    borderRadius: '8px',
                    padding: 'var(--spacing-sm) var(--spacing-md)',
                    cursor: 'pointer',
                    fontWeight: 'bold',
                    transition: 'transform 0.25s ease, box-shadow 0.25s ease, background-color 0.25s ease, border-color 0.25s ease',
                    fontSize: '0.9rem',
                    position: 'relative',
                    boxShadow: selectedModel === 'gemini-2.5-flash-lite' ? '0 0 10px var(--accent)' : 'none'
                  }}
                  onMouseEnter={(e) => {
                    if (selectedModel !== 'gemini-2.5-flash-lite') {
                      e.target.style.background = '#7c4dff';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (selectedModel !== 'gemini-2.5-flash-lite') {
                      e.target.style.background = '#6200ea';
                    }
                  }}
                  title="Vertex AI Gemini 2.5 Flash Lite - Uso Directo SDK"
                >
                  💎 Gemini 2.5 Flash Lite (Vertex AI)
                  {selectedModel === 'gemini-2.5-flash-lite' && (
                    <span style={{
                      position: 'absolute',
                      top: '-4px',
                      right: '-4px',
                      background: '#28a745',
                      color: 'white',
                      borderRadius: '50%',
                      width: '18px',
                      height: '18px',
                      fontSize: '11px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      ✓
                    </span>
                  )}
                </button>

                {Object.entries(availableModels).filter(([key]) => !key.startsWith('gemini-')).map(([key, model]) => (
                  <button
                    key={key}
                    onClick={() => handleModelChange(key)}
                    style={{
                      background: selectedModel === key ? 'var(--accent)' : 'var(--bg-tertiary)',
                      color: selectedModel === key ? 'white' : 'var(--text-primary)',
                      border: '1px solid var(--border)',
                      borderRadius: '8px',
                      padding: 'var(--spacing-sm) var(--spacing-md)',
                      cursor: 'pointer',
                      fontWeight: selectedModel === key ? 'bold' : 'normal',
                      transition: 'transform 0.25s ease, box-shadow 0.25s ease, background-color 0.25s ease, border-color 0.25s ease',
                      fontSize: '0.9rem',
                      position: 'relative'
                    }}
                    onMouseEnter={(e) => {
                      if (selectedModel !== key) {
                        e.target.style.background = 'var(--accent-hover)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (selectedModel !== key) {
                        e.target.style.background = 'var(--bg-tertiary)';
                      }
                    }}
                    title={`${model.displayName} - ${model.modelName}`}
                  >
                    {model.displayName}
                    {selectedModel === key && (
                      <span style={{
                        position: 'absolute',
                        top: '-4px',
                        right: '-4px',
                        background: '#28a745',
                        color: 'white',
                        borderRadius: '50%',
                        width: '16px',
                        height: '16px',
                        fontSize: '10px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        ✓
                      </span>
                    )}
                  </button>
                ))}
              </div>

              {currentModelInfo && (
                <div style={{
                  padding: 'var(--spacing-md)',
                  background: 'var(--bg-primary)',
                  borderRadius: '8px',
                  border: '1px solid var(--border)'
                }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: 'var(--spacing-sm)'
                  }}>
                    <strong style={{ color: 'var(--text-primary)' }}>
                      Modelo Seleccionado:
                    </strong>
                    <span style={{
                      background: 'var(--accent)',
                      color: 'white',
                      padding: 'var(--spacing-xs) var(--spacing-sm)',
                      borderRadius: '4px',
                      fontSize: '0.8rem',
                      fontWeight: 'bold'
                    }}>
                      {currentModelInfo.displayName}
                    </span>
                  </div>
                  <div style={{
                    fontSize: '0.85rem',
                    color: 'var(--text-secondary)',
                    lineHeight: '1.4'
                  }}>
                    <div><strong>ID del modelo:</strong> {currentModelInfo.modelName}</div>
                    {currentModelInfo.provider === 'lmstudio' ? (
                      <>
                        <div><strong>Servidor Local:</strong> http://127.0.0.1:1234/v1</div>
                        <div><strong>Costo:</strong> $0.00 (Inferencia Local en GPU)</div>
                      </>
                    ) : (
                      <>
                        {currentModelInfo.maxTokens && (
                          <div><strong>Tokens máximos:</strong> {currentModelInfo.maxTokens.toLocaleString()}</div>
                        )}
                        {currentModelInfo.costPer1kTokens && (
                          <div><strong>Costo por 1K tokens:</strong> ${currentModelInfo.costPer1kTokens}</div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div style={{
              textAlign: 'center',
              padding: 'var(--spacing-md)',
              color: 'var(--text-secondary)',
              fontStyle: 'italic'
            }}>
              No se encontraron modelos disponibles
            </div>
          )}
        </div>
      </div>

      <div style={{
        background: 'var(--bg-secondary)',
        borderRadius: '12px',
        padding: 'var(--spacing-lg)',
        marginBottom: 'var(--spacing-lg)',
        border: '1px solid var(--border)'
      }}>
        <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
          <button
            className={`tab-btn ${mode === 'general' ? 'active' : ''}`}
            onClick={() => setMode('general')}
            style={{ flex: 1, padding: '12px', borderRadius: '8px', border: '1px solid var(--accent)', background: mode === 'general' ? 'var(--accent)' : 'transparent', color: mode === 'general' ? 'white' : 'var(--accent)', cursor: 'pointer', fontWeight: 'bold' }}
          >
            🌐 Modo General
          </button>
          <button
            className={`tab-btn ${mode === 'specialty' ? 'active' : ''}`}
            onClick={() => setMode('specialty')}
            style={{ flex: 1, padding: '12px', borderRadius: '8px', border: '1px solid var(--accent)', background: mode === 'specialty' ? 'var(--accent)' : 'transparent', color: mode === 'specialty' ? 'white' : 'var(--accent)', cursor: 'pointer', fontWeight: 'bold' }}
          >
            ⭐ Modo Especialidad
          </button>
        </div>

        <div className="doc-config-section" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px' }}>
          <div className="form-group">
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>Cantidad de Preguntas:</label>
            <input
              type="number"
              value={numQuestions}
              onChange={(e) => setNumQuestions(Math.min(1000, Math.max(1, parseInt(e.target.value) || 10)))}
              min="1"
              max="1000"
              style={{ padding: '10px', borderRadius: '6px', border: '1px solid var(--border)', width: '100%', background: 'var(--bg-primary)', color: 'var(--text-primary)' }}
            />
            {recommendedQuestions > 0 && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '10px',
                flexWrap: 'wrap',
                marginTop: '8px',
                padding: '8px 10px',
                borderRadius: '6px',
                background: 'rgba(197, 160, 89, 0.10)',
                backgroundImage: 'linear-gradient(to right, rgba(197,160,89,0.14), transparent 45%)',
                fontSize: '0.8rem',
                color: 'var(--text-secondary)'
              }}>
                <span>
                  Según los <strong style={{ color: 'var(--gold-matte, var(--accent))' }}>{extractedText.length.toLocaleString('es-MX')}</strong> caracteres extraídos
                  se recomiendan <strong style={{ color: 'var(--gold-matte, var(--accent))' }}>{recommendedQuestions}</strong> preguntas.
                </span>
                {numQuestions !== recommendedQuestions && (
                  <button
                    type="button"
                    onClick={() => setNumQuestions(Math.min(1000, recommendedQuestions))}
                    style={{
                      flexShrink: 0,
                      padding: '6px 12px',
                      borderRadius: '5px',
                      background: 'rgba(197, 160, 89, 0.18)',
                      border: '1px solid rgba(197, 160, 89, 0.5)',
                      color: 'var(--gold-matte, var(--accent))',
                      fontWeight: 'bold',
                      fontSize: '0.75rem',
                      cursor: 'pointer'
                    }}
                  >
                    Usar {Math.min(1000, recommendedQuestions)}
                  </button>
                )}
              </div>
            )}
          </div>

          <div className="form-group">
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>Dificultad:</label>
            <select
              value={difficulty}
              onChange={(e) => setDifficulty(e.target.value)}
              style={{ padding: '10px', borderRadius: '6px', border: '1px solid var(--border)', width: '100%', background: 'var(--bg-primary)', color: 'var(--text-primary)' }}
            >
              <option value="easy">Fácil</option>
              <option value="medium">Media</option>
              <option value="hard">Difícil</option>
            </select>
          </div>

          {mode === 'general' ? (
            <>
              <div className="form-group">
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>Área:</label>
                <select
                  value={selectedAreaId}
                  onChange={(e) => setSelectedAreaId(e.target.value)}
                  style={{ padding: '10px', borderRadius: '6px', border: '1px solid var(--border)', width: '100%', background: 'var(--bg-primary)', color: 'var(--text-primary)' }}
                >
                  <option value="">Seleccionar área...</option>
                  {areas.map(a => <option key={a.id} value={a.id}>{a.nombre}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>Tema (destino en preguntas_por_tema):</label>
                <select
                  value={selectedGeneralTemaId}
                  onChange={(e) => setSelectedGeneralTemaId(e.target.value)}
                  style={{ padding: '10px', borderRadius: '6px', border: '1px solid var(--border)', width: '100%', background: 'var(--bg-primary)', color: 'var(--text-primary)' }}
                >
                  <option value="">Seleccionar tema...</option>
                  {temas.filter(t => !selectedAreaId || t.area_id === parseInt(selectedAreaId)).map(t => (
                    <option key={t.id} value={t.id}>#{t.id} - {t.nombre}</option>
                  ))}
                </select>
              </div>

              {/* Nuevos Selectores de Target Metadata en Modo General */}
              <div className="form-group">
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>Dirigido a (Jerarquía):</label>
                <select
                  value={specialtyJerarquia}
                  onChange={(e) => setSpecialtyJerarquia(e.target.value)}
                  style={{ padding: '10px', borderRadius: '6px', border: '1px solid var(--border)', width: '100%', background: 'var(--bg-primary)', color: 'var(--text-primary)' }}
                >
                  <option value="">Seleccionar Jerarquía...</option>
                  {hierarchyOptions.map(h => <option key={h.code} value={h.name}>{h.name}</option>)}
                </select>
              </div>

              <div className="form-group">
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>Cuerpo / Servicio:</label>
                <select
                  value={specialtyCuerpo}
                  onChange={(e) => { setSpecialtyCuerpo(e.target.value); setSpecialtyEspecialidad(''); }}
                  style={{ padding: '10px', borderRadius: '6px', border: '1px solid var(--border)', width: '100%', background: 'var(--bg-primary)', color: 'var(--text-primary)' }}
                >
                  <option value="">Seleccionar Cuerpo/Servicio...</option>
                  {corpsOptions.map(c => <option key={c.code} value={c.name}>{c.name}</option>)}
                </select>
              </div>

              <div className="form-group">
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>Especialidad Destino:</label>
                <select
                  value={specialtyEspecialidad}
                  onChange={(e) => setSpecialtyEspecialidad(e.target.value)}
                  style={{ padding: '10px', borderRadius: '6px', border: '1px solid var(--border)', width: '100%', background: 'var(--bg-primary)', color: 'var(--text-primary)' }}
                >
                  <option value="">Seleccionar Especialidad...</option>
                  {specialtyOptions.map(s => <option key={s.code + '-' + s.name.replace(/\s/g, '_')} value={s.name}>{s.name}</option>)}
                </select>
              </div>
            </>
          ) : (
            <>
              <div className="form-group">
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>Jerarquía:</label>
                <select
                  value={specialtyJerarquia}
                  onChange={(e) => setSpecialtyJerarquia(e.target.value)}
                  style={{ padding: '10px', borderRadius: '6px', border: '1px solid var(--border)', width: '100%', background: 'var(--bg-primary)', color: 'var(--text-primary)' }}
                >
                  <option value="">Seleccionar...</option>
                  {hierarchyOptions.map(h => <option key={h.code} value={h.name}>{h.name}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>Cuerpo / Servicio:</label>
                <select
                  value={specialtyCuerpo}
                  onChange={(e) => { setSpecialtyCuerpo(e.target.value); setSpecialtyEspecialidad(''); }}
                  style={{ padding: '10px', borderRadius: '6px', border: '1px solid var(--border)', width: '100%', background: 'var(--bg-primary)', color: 'var(--text-primary)' }}
                >
                  <option value="">Seleccionar...</option>
                  {corpsOptions.map(c => <option key={c.code} value={c.name}>{c.name}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>Especialidad:</label>
                <select
                  value={specialtyEspecialidad}
                  onChange={(e) => setSpecialtyEspecialidad(e.target.value)}
                  style={{ padding: '10px', borderRadius: '6px', border: '1px solid var(--border)', width: '100%', background: 'var(--bg-primary)', color: 'var(--text-primary)' }}
                >
                  <option value="">Seleccionar...</option>
                  {specialtyOptions.map(s => <option key={s.code + '-' + s.name.replace(/\s/g, '_')} value={s.name}>{s.name}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>Tema de Especialidad:</label>
                <select
                  value={specialtyTemaId}
                  onChange={(e) => setSpecialtyTemaId(e.target.value)}
                  style={{ padding: '10px', borderRadius: '6px', border: '1px solid var(--border)', width: '100%', background: 'var(--bg-primary)', color: 'var(--text-primary)' }}
                >
                  <option value="">Seleccionar tema...</option>
                  {specialtyTemasLoading
                    ? <option disabled>⏳ Cargando temas...</option>
                    : Object.values(specialtyTemas[specialtyJerarquia]?.[specialtyCuerpo]?.[specialtyEspecialidad]?.temas || {}).map(t => <option key={t.id} value={t.id}>{t.nombre}</option>)}
                </select>
              </div>
            </>
          )}
        </div>

        <div style={{ marginTop: '30px', textAlign: 'center' }}>
          <button
            ref={fileInputRef}
            onClick={() => document.getElementById('vortex-file-input').click()}
            style={{ background: 'var(--accent)', color: 'white', border: 'none', borderRadius: '8px', padding: '15px 30px', cursor: 'pointer', fontWeight: 'bold', fontSize: '1.1rem' }}
          >
            📁 {fileName ? 'Cambiar Archivo' : 'Cargar Documento'}
          </button>
          <input
            id="vortex-file-input"
            type="file"
            accept=".pdf,.docx,.txt"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />
          {fileName && (
            <div style={{ marginTop: '10px' }}>
              <p style={{ color: 'var(--text-secondary)', marginBottom: '6px' }}>📄 {fileName}</p>
              {extractedText && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  <span style={{
                    display: 'inline-flex', alignItems: 'center', gap: '6px',
                    padding: '6px 12px', borderRadius: '6px',
                    background: 'var(--bg-primary)', border: '1px solid var(--border)',
                    fontSize: '0.82rem', color: 'var(--text-primary)'
                  }}>
                    📝 Texto extraído: <strong style={{ color: 'var(--gold-matte, var(--accent))' }}>{extractedText.length.toLocaleString('es-MX')}</strong> caracteres
                  </span>
                  <span style={{
                    display: 'inline-flex', alignItems: 'center', gap: '6px',
                    padding: '6px 12px', borderRadius: '6px',
                    background: 'rgba(197, 160, 89, 0.12)',
                    border: '1px solid rgba(197, 160, 89, 0.4)',
                    fontSize: '0.82rem', color: 'var(--text-primary)'
                  }}>
                    🎯 Recomendadas: <strong style={{ color: 'var(--gold-matte, var(--accent))' }}>{recommendedQuestions}</strong> preguntas
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Paso de Generación */}
      <div style={{
        background: 'var(--bg-secondary)',
        borderRadius: '12px',
        padding: 'var(--spacing-lg)',
        marginBottom: 'var(--spacing-lg)',
        border: '1px solid var(--border)',
        textAlign: 'center'
      }}>
        <button
          onClick={generateQuiz}
          disabled={!extractedText || generating}
          style={{ background: extractedText && !generating ? 'var(--accent)' : 'var(--bg-tertiary)', color: 'white', border: 'none', borderRadius: '8px', padding: '15px 40px', cursor: 'pointer', fontWeight: 'bold', fontSize: '1.2rem', boxShadow: 'var(--shadow-md)' }}
        >
          {generating ? '⚙️ Generando...' : '🚀 Lanzar Vortex IA'}
        </button>
        {generating && (
          <div style={{ marginTop: '20px' }}>
            <p>{generationStep}</p>
            <div style={{ width: '100%', height: '8px', background: 'var(--bg-tertiary)', borderRadius: '4px', overflow: 'hidden', marginTop: '10px' }}>
              <div style={{ width: `${generationProgress}%`, height: '100%', background: 'var(--accent)', transition: 'width 0.3s ease' }}></div>
            </div>
          </div>
        )}
      </div>

      {/* RESULTADO INTERACTIVO (PREVIEW) */}
      {generatedQuestions.length > 0 && (
        <div className="interactive-preview" style={{ marginTop: '30px' }}>
          <h2 style={{ textAlign: 'center', marginBottom: '30px' }}>⚡ Cuestionario Generado</h2>
          <div style={{ display: 'grid', gap: '20px' }}>
            {generatedQuestions.map((q, qIndex) => (
              <div key={qIndex} style={{ background: 'var(--bg-secondary)', borderRadius: '12px', padding: '20px', border: '1px solid var(--border)', boxShadow: 'var(--shadow-sm)' }}>
                <h4 style={{ marginBottom: '15px', color: 'var(--text-primary)' }}>{qIndex + 1}. {q.question}</h4>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '10px' }}>
                  {q.options.map((opt, optIndex) => (
                    <div
                      key={optIndex}
                      style={{
                        padding: '12px',
                        borderRadius: '8px',
                        border: '1px solid var(--border)',
                        background: optIndex === q.correctAnswer ? 'rgba(40, 167, 69, 0.1)' : 'var(--bg-primary)',
                        borderColor: optIndex === q.correctAnswer ? '#28a745' : 'var(--border)',
                        color: 'var(--text-primary)',
                        position: 'relative'
                      }}
                    >
                      {String.fromCharCode(65 + optIndex)}) {opt}
                      {optIndex === q.correctAnswer && <span style={{ position: 'absolute', right: '10px', color: '#28a745' }}>⭐</span>}
                    </div>
                  ))}
                </div>
                {q.explanation && (
                  <p style={{ marginTop: '15px', fontSize: '0.9rem', color: 'var(--text-secondary)', fontStyle: 'italic', borderTop: '1px solid var(--border)', paddingTop: '10px' }}>
                    💡 {q.explanation}
                  </p>
                )}
              </div>
            ))}
          </div>

          <div style={{ marginTop: '40px', background: 'var(--bg-tertiary)', padding: '30px', borderRadius: '15px', textAlign: 'center' }}>
            <h3 style={{ marginBottom: '20px' }}>🏁 ¿Deseas publicar este contenido?</h3>
            <div style={{ display: 'flex', gap: '15px', flexWrap: 'wrap', justifyContent: 'center' }}>
              <button
                onClick={handlePublishToPreguntasPorTema}
                disabled={isLoading || !selectedGeneralTemaId || mode !== 'general'}
                style={{ background: '#d32f2f', color: 'white', border: 'none', borderRadius: '8px', padding: '12px 25px', cursor: 'pointer', fontWeight: 'bold', opacity: (!selectedGeneralTemaId || mode !== 'general') ? 0.5 : 1 }}
                title={mode !== 'general' ? 'Cambia a Modo General y selecciona un tema' : !selectedGeneralTemaId ? 'Selecciona un tema destino' : ''}
              >
                📥 preguntas_por_tema/{selectedGeneralTemaId || '...'}
              </button>

              <button
                onClick={() => {
                  saveToAdminCache(generatedQuestions);
                  setSuccess('Caché de administración actualizada con todas las preguntas.');
                }}
                disabled={isLoading}
                style={{ background: '#388e3c', color: 'white', border: 'none', borderRadius: '8px', padding: '12px 25px', cursor: 'pointer', fontWeight: 'bold' }}
              >
                💾 Actualizar Caché Admin
              </button>

              <button
                onClick={handleSaveToAccount}
                disabled={isLoading}
                style={{ background: '#4a90e2', color: 'white', border: 'none', borderRadius: '8px', padding: '12px 25px', cursor: 'pointer', fontWeight: 'bold' }}
              >
                📂 Mis Cuestionarios
              </button>

              <button
                onClick={handleSaveToVault}
                disabled={isVaultPublishing}
                style={{ background: '#6200ea', color: 'white', border: 'none', borderRadius: '8px', padding: '12px 25px', cursor: 'pointer', fontWeight: 'bold' }}
              >
                🛡️ Bóveda Técnica (Firestore)
              </button>

              <button
                onClick={handlePublishToGlobalVault}
                disabled={isGlobalVaultPublishing}
                style={{ background: '#009688', color: 'white', border: 'none', borderRadius: '8px', padding: '12px 25px', cursor: 'pointer', fontWeight: 'bold' }}
              >
                ☁️ Bóveda Compartida (Global)
              </button>

              <button
                onClick={handlePublishSpecialtyQuizz}
                disabled={isLoading}
                style={{ background: '#f57c00', color: 'white', border: 'none', borderRadius: '8px', padding: '12px 25px', cursor: 'pointer', fontWeight: 'bold' }}
              >
                🎗️ Cuestionario Oficial (RTDB)
              </button>

              <button
                onClick={handlePublishToAcademy}
                disabled={isVaultPublishing || !generatedQuestions.length}
                style={{ background: 'var(--gold-matte)', color: 'white', border: 'none', borderRadius: '8px', padding: '12px 25px', cursor: 'pointer', fontWeight: 'bold', opacity: !generatedQuestions.length ? 0.5 : 1 }}
              >
                🎓 Publicar a Vortex Academy
              </button>

              <button
                onClick={() => setGeneratedQuestions([])}
                style={{ background: '#6c757d', color: 'white', border: 'none', borderRadius: '8px', padding: '12px 25px', cursor: 'pointer', fontWeight: 'bold' }}
              >
                🔄 Descartar y Reintentar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Botón Limpiar Todo */}
      <div style={{
        textAlign: 'center',
        marginTop: 'var(--spacing-xl)'
      }}>
        <button
          onClick={clearAll}
          style={{
            background: '#dc3545',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            padding: 'var(--spacing-md) var(--spacing-lg)',
            cursor: 'pointer',
            fontWeight: 'bold',
            transition: 'transform 0.25s ease, box-shadow 0.25s ease, background-color 0.25s ease, border-color 0.25s ease'
          }}
          onMouseEnter={(e) => e.target.style.background = '#c82333'}
          onMouseLeave={(e) => e.target.style.background = '#dc3545'}
        >
          🗑️ Limpiar Todo
        </button>
      </div>

      {/* Mensajes de estado */}
      {error && (
        <div style={{
          background: '#f8d7da',
          color: '#721c24',
          padding: 'var(--spacing-md)',
          borderRadius: '8px',
          marginTop: 'var(--spacing-md)',
          border: '1px solid #f5c6cb'
        }}>
          ❌ {error}
        </div>
      )}

      {success && (
        <div style={{
          background: '#d4edda',
          color: '#155724',
          padding: 'var(--spacing-md)',
          borderRadius: '8px',
          marginTop: 'var(--spacing-md)',
          border: '1px solid #c3e6cb'
        }}>
          ✅ {success}
        </div>
      )}
    </div>
  );
};

export default VortexDocs; 